import { ParamValues } from '../../../../components/Crud/useFilter.types';
import SearchStringUtil from '../../../../utils/searchString.utils';
import { saleItemIsKit, saleItemIsProductColor } from '../../../catalogs/utils/saleItemType.utils';
import {
  OfflineDatabaseCatalogSection,
  OfflineDatabaseCatalogSectionItemFilterOption,
  OfflineDatabaseCatalogSectionKit,
  OfflineDatabaseCatalogSectionProduct,
} from '../../database/offlineDatabase/types/offlineDatabaseCatalog.types';
import OfflinePriceTableUtils from '../../repositories/utils/offlinePriceTable.utils';
import {
  KitMatchesFilterParams,
  MatchesFilterParams,
  ProductMatchesFilterParams,
} from '../types/catalogProductsFilter.utils.types';
import {
  KitFilterOptionsKey,
  KitKeyToParamMap,
  ParamsKey,
  ProductFilterOptionsKey,
  ProductKeyToParamMap,
} from '../types/getCatalogSections.utils.types';

export default class CatalogItemsFilterUtils {
  private static readonly baseKeyToParamMapper = [
    { key: 'colorIds', paramKey: 'productColorIds' },
    { key: 'categoryIds', paramKey: 'productCategoryIds' },
    { key: 'collectionIds', paramKey: 'collectionsIds' },
    { key: 'sizeIds', paramKey: 'sizeOptionsIds' },
    { key: 'referenceColorIds', paramKey: 'colorReferencesIds' },
    { key: 'classificationIds', paramKey: 'classificationsIds' },
    { key: 'deliveryWindowIds', paramKey: 'deliveryWindowIds' },
    { key: 'availabilities', paramKey: 'availability' },
    { key: 'priceTableIdsWithPromotion', paramKey: 'priceTableIdToFilterPromotion' },
    { key: 'priceTableIdsWithZeroPrice', paramKey: 'priceTableIdToFilterZeroPrice' },
    { key: 'itemCodeAndName', paramKey: 'codeOrName' },
  ] as const satisfies readonly {
    key: ProductFilterOptionsKey | KitFilterOptionsKey;
    paramKey: ParamsKey;
  }[];

  private static readonly productkeyToParamMapper: ProductKeyToParamMap[] = [
    ...this.baseKeyToParamMapper,
  ];

  private static readonly kitKeyToParamMapper: KitKeyToParamMap[] = [...this.baseKeyToParamMapper];

  static async filterCatalogSectionProducts(
    catalogSections: OfflineDatabaseCatalogSection[],
    params: ParamValues,
  ): Promise<string[]> {
    const sectionsWithOnlyProducts = catalogSections.map((section) => ({
      ...section,
      items: section.items.filter((item) => saleItemIsProductColor(item)),
    }));
    const formattedParams = await this.formatParams(params);

    const filteredProductColorIds: string[] = [];

    sectionsWithOnlyProducts.forEach((section) => {
      section.items.forEach((sectionProduct) => {
        if (this.filterProduct(sectionProduct, formattedParams)) {
          filteredProductColorIds.push(sectionProduct.productColor.id);
        }
      });
    });

    return filteredProductColorIds;
  }

  static async filterCatalogSectionKits(
    catalogSections: OfflineDatabaseCatalogSection[],
    params: ParamValues,
  ): Promise<string[]> {
    const kitsOnSections = catalogSections
      .map((section) => section.items.filter((item) => saleItemIsKit(item)))
      .flat();
    const formattedParams = await this.formatParams(params);

    return kitsOnSections
      .map((sectionKit) => {
        if (this.filterKit(sectionKit, formattedParams)) {
          return sectionKit.kit.id;
        }
        return null;
      })
      .filter((kitId) => kitId !== null) as string[];
  }

  private static async formatParams(params: ParamValues): Promise<ParamValues> {
    const priceTableId = await this.getPriceTableId(params);

    const formattedParams: ParamValues = {
      ...params,
      priceTableId: priceTableId,
      priceTableIdToFilterZeroPrice: [priceTableId],
    };

    if (['true', 'false'].includes(params.productsWithPromotion as string)) {
      return {
        ...formattedParams,
        priceTableIdToFilterPromotion: [priceTableId],
      };
    }

    return formattedParams;
  }

  private static async getPriceTableId(params: ParamValues): Promise<string> {
    const orderId = params.orderId as string | undefined;
    return OfflinePriceTableUtils.getOrderOrDefaultPriceTableId(orderId);
  }

  private static filterItem<
    TItem extends OfflineDatabaseCatalogSectionKit | OfflineDatabaseCatalogSectionProduct,
    TKey extends KitFilterOptionsKey | ProductFilterOptionsKey,
  >(
    item: TItem,
    params: ParamValues,
    keyToParamMapper: Array<{ key: TKey; paramKey: keyof ParamValues }>,
    matcherFn: (params: any) => boolean,
  ): boolean {
    const { filterOptions } = item;
    const normalizedParams = this.normalizeParams(params);
    const promotionParamValue = normalizedParams.productsWithPromotion;

    return keyToParamMapper.every(({ key, paramKey }) => {
      const paramValues = normalizedParams[paramKey];
      if (!paramValues?.length) {
        return true;
      }
      const values = filterOptions[key] as string[];
      return matcherFn({ key, values, paramValues, promotionParamValue });
    });
  }

  private static filterKit(
    sectionKit: OfflineDatabaseCatalogSectionKit,
    params: ParamValues,
  ): boolean {
    const passesBaseFilters = this.filterItem(
      sectionKit,
      params,
      this.kitKeyToParamMapper,
      this.kitMatchesFilter.bind(this),
    );

    if (!passesBaseFilters) {
      return false;
    }

    return this.matchesSellInPriceFilter(sectionKit.filterOptions, params);
  }

  private static filterProduct(
    sectionProduct: OfflineDatabaseCatalogSectionProduct,
    params: ParamValues,
  ): boolean {
    const passesBaseFilters = this.filterItem(
      sectionProduct,
      params,
      this.productkeyToParamMapper,
      this.productMatchesFilter.bind(this),
    );

    if (!passesBaseFilters) {
      return false;
    }

    return this.matchesSellInPriceFilter(sectionProduct.filterOptions, params);
  }

  private static normalizeParams(params: ParamValues) {
    return Object.fromEntries(
      Object.entries(params).map(([key, value]) => [key, this.toStringArray(value)]),
    );
  }

  private static matchesFilter({
    key,
    values,
    paramValues,
    promotionParamValue,
  }: MatchesFilterParams): boolean {
    const codeOrNameFilter = (value: string) => {
      const normalizedValue = SearchStringUtil.normalize(value);
      const searchWords = SearchStringUtil.prepareForSearch(paramValues);
      return searchWords.every((word) => normalizedValue.includes(word));
    };

    const defaultFilter = (value: string) => paramValues.includes(value);

    if (key === 'itemCodeAndName') {
      return values.some(codeOrNameFilter);
    }
    if (key === 'priceTableIdsWithZeroPrice') {
      return !values.some(defaultFilter);
    }
    if (key === 'priceTableIdsWithPromotion') {
      const hasPromotion = values.some(defaultFilter);
      const filterValue = promotionParamValue?.[0];
      if (filterValue === 'true') {
        return hasPromotion;
      }
      if (filterValue === 'false') {
        return !hasPromotion;
      }
      return true;
    }
    return values.some(defaultFilter);
  }

  private static kitMatchesFilter(params: KitMatchesFilterParams) {
    return this.matchesFilter(params);
  }

  private static productMatchesFilter(params: ProductMatchesFilterParams): boolean {
    return this.matchesFilter(params);
  }

  private static toStringArray(param?: ParamValues[string]): string[] {
    if (!param) {
      return [];
    }
    if (!Array.isArray(param)) {
      return [String(param)];
    }
    return param;
  }

  private static matchesSellInPriceFilter(
    filterOptions: OfflineDatabaseCatalogSectionItemFilterOption,
    params: ParamValues,
  ): boolean {
    const priceMin = params.sellInPriceMin;
    const priceMax = params.sellInPriceMax;

    if (priceMin == null && priceMax == null) {
      return true;
    }

    const priceTableId = params.priceTableId as string | undefined;
    if (!priceTableId) {
      return true;
    }

    if (!filterOptions.sellInPriceByPriceTable) {
      return true;
    }

    const itemRange = filterOptions.sellInPriceByPriceTable[priceTableId];
    if (!itemRange) {
      return true;
    }

    const minValue = priceMin !== undefined && priceMin !== '' ? Number(priceMin) : undefined;
    const maxValue = priceMax !== undefined && priceMax !== '' ? Number(priceMax) : undefined;

    if (maxValue !== undefined && itemRange.min > maxValue) {
      return false;
    }

    if (minValue !== undefined && itemRange.max < minValue) {
      return false;
    }

    return true;
  }
}
